import React from 'react';
import styled from 'styled-components/macro';
import TruncateMarkup from 'react-truncate-markup';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { generatePath } from 'react-router-dom';
import { ButtonLink } from 'components/atoms/ButtonLink/ButtonLink';
import { HomeFeedSectionTitle } from 'components/atoms/HomeFeedSectionTitle/HomeFeedSectionTitle';
import { Button, ButtonEnums } from 'components/atoms/Button/Button';
import { Link } from 'components/atoms/Link/Link';
import { LogInRegisterButtons } from 'components/molecules/LogInRegisterButtons';
import { IC_BOOKMARK_BORDER } from 'resources/images';
import { getContentUrl, getPath } from 'utils';
import { Routes } from 'constants/index';

interface Props {
  isAuth: boolean;
  isDisplayedFromMegaMenu?: boolean;
  posts: Partial<State.ContentItem>[] | null;
  setBurger?: (state: boolean) => void;
}

export const BlogTopSavedPosts: React.FC<Props> = ({ isAuth, isDisplayedFromMegaMenu, posts, setBurger }) => {
  const dispatch = useDispatch();

  const navigateToPostItem = React.useCallback(
    (postItem?: Partial<State.ContentItem>) => {
      if (postItem) {
        if (setBurger) setBurger(false);
        dispatch(push(getContentUrl(postItem as State.ContentCardItem)));
      }
    },
    [dispatch, setBurger]
  );

  const navigateToSavedItemsPage = React.useCallback(() => {
    if (setBurger) setBurger(false);
    dispatch(push(getPath(Routes.PROFILE_SAVED_ITEMS)));
  }, [dispatch, setBurger]);

  return (
    <>
      <StyledTitleWrapper>
        <HomeFeedSectionTitle title="My Saved Blog Posts" testId="My-Saved-Blog-Posts-title" />
      </StyledTitleWrapper>

      {isAuth ? (
        <LoggedInBlogTopSavedPostsContent
          isDisplayedFromMegaMenu={isDisplayedFromMegaMenu}
          posts={posts}
          navigateToPostItem={navigateToPostItem}
          navigateToSavedItemsPage={navigateToSavedItemsPage}
        />
      ) : (
        <LoggedOutBlogTopSavedPostsContent isDisplayedFromMegaMenu={isDisplayedFromMegaMenu} />
      )}
    </>
  );
};

const LoggedInBlogTopSavedPostsContent: React.FC<{
  isDisplayedFromMegaMenu?: boolean;
  posts: Partial<State.ContentItem>[] | null;
  navigateToPostItem: (postItem?: Partial<State.ContentItem>) => void;
  navigateToSavedItemsPage: () => void;
}> = ({ isDisplayedFromMegaMenu = false, posts = [], navigateToPostItem, navigateToSavedItemsPage }) => {
  if (posts?.length) {
    return (
      <StyledLinksWrapper>
        {posts?.map((post: Partial<State.ContentItem>) => (
          <StyledLinkItemWrapper key={post.id}>
            {isDisplayedFromMegaMenu ? (
              <StyledLinkItemButton
                testId={post.title || ''}
                variant={ButtonEnums.variants.link}
                // tslint:disable-next-line: jsx-no-lambda
                onClick={() => navigateToPostItem(post)}
              >
                <TruncateMarkup ellipsis={'…'} lines={2}>
                  <div>{post.title}</div>
                </TruncateMarkup>
              </StyledLinkItemButton>
            ) : (
              <StyledLinkItem
                testId={post.title}
                isExternal={Boolean(post?.externalUrl)}
                to={post?.externalUrl || getContentUrl(post as State.ContentCardItem)}
              >
                <TruncateMarkup ellipsis={'…'} lines={2}>
                  <div>{post.title}</div>
                </TruncateMarkup>
              </StyledLinkItem>
            )}
          </StyledLinkItemWrapper>
        ))}
        {isDisplayedFromMegaMenu ? (
          <StyledLinkItemButton
            testId="view-all-saved-blog"
            variant={ButtonEnums.variants.link}
            // tslint:disable-next-line: jsx-no-lambda
            onClick={() => navigateToSavedItemsPage()}
          >
            <StyledIcon src={IC_BOOKMARK_BORDER} alt="go to my saved items" />
            View all Saved Blog posts
          </StyledLinkItemButton>
        ) : (
          <StyledLinkItem testId="view-all-saved-blog" to={getPath(Routes.PROFILE_SAVED_ITEMS)}>
            <StyledIcon src={IC_BOOKMARK_BORDER} alt="go to my saved items" />
            View all Saved Blog posts
          </StyledLinkItem>
        )}
      </StyledLinksWrapper>
    );
  }

  return <StyledTextWrapper>To save a post for future reading, select the bookmark icon.</StyledTextWrapper>;
};

const LoggedOutBlogTopSavedPostsContent: React.FC<{ isDisplayedFromMegaMenu?: boolean }> = ({
  isDisplayedFromMegaMenu = false,
}) => (
  <>
    <StyledTextWrapper>Log in or register to save posts for future reading.</StyledTextWrapper>
    {isDisplayedFromMegaMenu ? (
      <StyledLogInRegisterButtonsWrapper>
        <LogInRegisterButtons logInButtonTestId="login" registerButtonTestId="register" />
      </StyledLogInRegisterButtonsWrapper>
    ) : (
      <StyledAuthButtonGroupWrapper>
        <StyledAuthButton
          testId="login"
          to={generatePath(getPath(Routes.LOGIN_HASH))}
          variant={ButtonEnums.variants.primary}
          size={ButtonEnums.sizes.medium}
          bordered
          fluid
        >
          Log in
        </StyledAuthButton>
        <StyledAuthButton
          testId="register"
          to={generatePath(getPath(Routes.REGISTRATION_HASH))}
          variant={ButtonEnums.variants.secondary}
          size={ButtonEnums.sizes.medium}
          bordered
          fluid
        >
          Register
        </StyledAuthButton>
      </StyledAuthButtonGroupWrapper>
    )}
  </>
);

const StyledTitleWrapper = styled.div`
  margin-bottom: ${props => props.theme.pxToRem(8)};
`;

const StyledTextWrapper = styled.div`
  color: ${props => props.theme.colors.neutralGrey8};
  font-size: ${props => props.theme.fontSizes.xs};
  line-height: ${props => props.theme.pxToRem(22)};
`;

const StyledAuthButtonGroupWrapper = styled.div`
  margin-top: ${props => props.theme.pxToRem(16)};
`;

const StyledAuthButton = styled(ButtonLink)`
  &&&& {
    display: block;
    width: ${props => props.theme.pxToRem(116)};

    :first-child {
      margin-bottom: ${props => props.theme.pxToRem(12)};
    }
  }
`;

const StyledLinksWrapper = styled.div`
  ${props => props.theme.mediaQueries.mobileOnly} {
    margin-left: ${props => props.theme.pxToRem(20)};
  }
`;

const StyledLinkItemWrapper = styled.div`
  margin-bottom: ${props => props.theme.pxToRem(10)};
`;

const StyledLinkItem = styled(Link)`
  font-size: ${props => props.theme.fontSizes.xs};
  line-height: ${props => props.theme.pxToRem(22)};
`;

const StyledLinkItemButton = styled(Button)`
  &&&& {
    font-size: ${props => props.theme.fontSizes.xs};
    line-height: ${props => props.theme.pxToRem(22)};

    div {
      text-decoration: underline;
    }

    &:hover,
    &:active,
    &:focus {
      div {
        text-decoration: none;
      }
    }
  }
`;

const StyledIcon = styled.img`
  vertical-align: bottom;
  width: ${props => props.theme.pxToRem(18)};
  height: ${props => props.theme.pxToRem(18)};
  line-height: ${props => props.theme.pxToRem(18)};
  display: inline-block;
  margin-right: ${props => props.theme.pxToRem(4)};
`;

const StyledLogInRegisterButtonsWrapper = styled.div`
  margin-top: ${props => props.theme.pxToRem(16)};

  & > div > div {
    display: block;
  }

  &&&& {
    button {
      display: block;

      :first-child {
        margin-right: ${props => props.theme.pxToRem(0)};
        margin-bottom: ${props => props.theme.pxToRem(12)};

        ${props => props.theme.mediaQueries.mobileOnly} {
          margin-right: ${props => props.theme.pxToRem(0)};
        }
      }
    }
  }
`;
